'use client';

import { useState, useEffect, useRef } from 'react';

const SERVICE_TAGS = [
  'Brand Strategy',
  'Visual Identity',
  'Digital Campaigns',
  'Film Production',
  'Billboard Advertising',
  'Garment Manufacturing',
  'Content Strategy',
  'Media Relations',
  'Corporate Films',
  'Web Development',
  'Social Media',
  'Reputation Management',
];

export default function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentWord, setCurrentWord] = useState(0);
  const [showReelOpen, setShowReelOpen] = useState(false);
  const tickerRef = useRef<HTMLDivElement>(null);

  const rotatingWords = [
    'Credibility',
    'Markets',
    'Brand Equity',
    'Perception',
    'Trust',
  ];

  // -------------------------------------------------------------------------
  // Load animation
  // -------------------------------------------------------------------------
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // -------------------------------------------------------------------------
  // Rotating word ticker – cycles through the `rotatingWords` array
  // -------------------------------------------------------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % rotatingWords.length);
    }, 2800);
    return () => clearInterval(interval);
  }, [rotatingWords]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col overflow-hidden bg-[#0D0D0D]"
    >
      {/* Background Image – Full Width */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dramatic%20aerial%20cinematic%20view%20of%20modern%20Kampala%20Uganda%20city%20skyline%20at%20golden%20hour%20with%20warm%20sunset%20lighting%20contemporary%20buildings%20rolling%20green%20hills%20professional%20photography%20high%20contrast%20wide%20angle%20lens%20urban%20landscape%20East%20African%20metropolitan%20atmosphere%20dramatic%20orange%20and%20teal%20sky%20architectural%20details%20vibrant%20colors&width=1920&height=1080&seq=hero-fullbg-003&orientation=landscape"
          alt="Kampala Cityscape"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/85 to-[#0D0D0D]/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-[#0D0D0D]/60"></div>
      </div>

      {/* Subtle Grid Overlay */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(#038C7F 1px, transparent 1px), linear-gradient(90deg, #038C7F 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        ></div>
      </div>

      {/* Floating Particles – hidden on small screens */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              backgroundColor: i % 2 === 0 ? '#038C7F' : '#F2780C',
              opacity: 0.3,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${6 + Math.random() * 8}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Main Hero Content */}
      <div className="relative z-10 flex-1 flex items-center pt-20 sm:pt-24 lg:pt-0">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left side – text */}
            <div className="lg:col-span-7">
              <div
                className={`transition-all duration-1000 ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                {/* Headline */}
                <h1 className="mb-5 sm:mb-6">
                  <div className="overflow-hidden">
                    <div
                      className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[6.5rem] font-black text-white leading-[0.85] tracking-tighter transition-all duration-700 delay-100 ${
                        isLoaded ? 'translate-y-0' : 'translate-y-full'
                      }`}
                    >
                      Asili
                    </div>
                  </div>
                  <div className="overflow-hidden mt-1">
                    <div
                      className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[6.5rem] font-black leading-[0.85] tracking-tighter transition-all duration-700 delay-200 ${
                        isLoaded ? 'translate-y-0' : 'translate-y-full'
                      }`}
                    >
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] via-[#05a99a] to-[#F2780C]">
                        Visuals
                      </span>
                    </div>
                  </div>
                </h1>

                {/* Tagline */}
                <div
                  className={`flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6 transition-all duration-700 delay-300 ${
                    isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                  }`}
                >
                  <div className="w-10 sm:w-14 h-[2px] bg-gradient-to-r from-[#038C7F] to-[#F2780C] mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="text-lg sm:text-xl md:text-2xl font-serif italic text-white/90 leading-snug block">
                      Authentic by Design.
                    </span>
                    <span className="text-lg sm:text-xl md:text-2xl font-serif italic text-[#F2780C]/90 leading-snug block">
                      Impactful by Nature.
                    </span>
                  </div>
                </div>

                {/* Description with rotating word */}
                <p
                  className={`text-sm sm:text-base text-gray-400 leading-relaxed max-w-lg mb-6 sm:mb-8 transition-all duration-700 delay-400 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                >
                  An integrated advertising and brand communication firm based in
                  Kampala, building strategic communication systems that strengthen{' '}
                  <span className="relative inline-block">
                    <span
                      key={currentWord}
                      className="text-[#038C7F] font-semibold inline-block animate-fadeSlideUp"
                    >
                      {rotatingWords[currentWord]}
                    </span>
                  </span>{' '}
                  across East Africa.
                </p>

                {/* CTA Buttons */}
                <div
                  className={`flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-10 transition-all duration-700 delay-500 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                >
                  <a
                    href="#portfolio"
                    className="group relative px-7 sm:px-8 py-3.5 sm:py-4 bg-[#038C7F] text-white text-sm font-bold rounded-full overflow-hidden whitespace-nowrap cursor-pointer text-center"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      View Our Work
                      <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
                    </span>
                    <div className="absolute inset-0 bg-[#F2780C] translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  </a>

                  <button
                    onClick={() => setShowReelOpen(true)}
                    className="group px-7 sm:px-8 py-3.5 sm:py-4 border-2 border-white/20 text-white text-sm font-bold rounded-full hover:border-[#F2780C] hover:bg-[#F2780C]/10 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-3"
                  >
                    <span className="relative w-8 h-8 flex items-center justify-center">
                      <span className="absolute inset-0 bg-white/20 rounded-full animate-ping opacity-40"></span>
                      <span className="relative w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <i className="ri-play-fill text-base text-white ml-0.5"></i>
                      </span>
                    </span>
                    Watch Showreel
                  </button>
                </div>

                {/* Badges */}
                <div
                  className={`flex flex-wrap items-center gap-3 sm:gap-4 transition-all duration-700 delay-600 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                >
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 border border-white/10 rounded-full">
                    <span className="w-2 h-2 bg-[#038C7F] rounded-full animate-pulse"></span>
                    <span className="text-[10px] sm:text-xs font-semibold text-white/70 tracking-wider uppercase">
                      Kampala, Uganda
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 border border-white/10 rounded-full">
                    <i className="ri-global-line text-[#F2780C] text-xs"></i>
                    <span className="text-[10px] sm:text-xs font-semibold text-white/70 tracking-wider uppercase">
                      Serving East Africa
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 border border-white/10 rounded-full">
                    <i className="ri-award-line text-[#038C7F] text-xs"></i>
                    <span className="text-[10px] sm:text-xs font-semibold text-white/70 tracking-wider uppercase">
                      Est. 2020
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side – stats (desktop only) */}
            <div className="lg:col-span-5 hidden lg:flex flex-col items-end justify-center gap-6">
              {/* Stats cards */}
              <div
                className={`transition-all duration-700 delay-500 ${
                  isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      value: '150+',
                      label: 'Projects Delivered',
                      icon: 'ri-folder-chart-line',
                      color: '#038C7F',
                    },
                    {
                      value: '5',
                      label: 'Countries Served',
                      icon: 'ri-earth-line',
                      color: '#F2780C',
                    },
                    {
                      value: '360°',
                      label: 'Brand Solutions',
                      icon: 'ri-refresh-line',
                      color: '#F2780C',
                    },
                    {
                      value: '6+',
                      label: 'Core Services',
                      icon: 'ri-service-line',
                      color: '#038C7F',
                    },
                  ].map((stat, idx) => (
                    <div
                      key={idx}
                      className="group bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 cursor-default"
                      style={{ animationDelay: `${idx * 100}ms` }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                        style={{ backgroundColor: `${stat.color}20` }}
                      >
                        <i className={`${stat.icon} text-lg`} style={{ color: stat.color }}></i>
                      </div>
                      <div className="text-2xl xl:text-3xl font-black text-white mb-0.5">
                        {stat.value}
                      </div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service chips */}
              <div
                className={`flex flex-wrap justify-end gap-2 max-w-sm transition-all duration-700 delay-700 ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                }`}
              >
                {['Branding', 'Film', 'Digital', 'Outdoor'].map((tag, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-[10px] font-semibold text-white/50 uppercase tracking-wider hover:text-white/80 hover:border-white/20 transition-all cursor-default"
                  >
                    {tag}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile stats row */}
          <div
            className={`lg:hidden flex items-center gap-4 sm:gap-6 mt-8 pt-6 border-t border-white/10 transition-all duration-700 delay-700 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div>
              <div className="text-xl sm:text-2xl font-black text-white">
                150<span className="text-[#038C7F]">+</span>
              </div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-wider">
                Projects
              </div>
            </div>
            <div className="w-px h-8 sm:h-10 bg-white/10"></div>
            <div>
              <div className="text-xl sm:text-2xl font-black text-white">5</div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-wider">
                Countries
              </div>
            </div>
            <div className="w-px h-8 sm:h-10 bg-white/10"></div>
            <div>
              <div className="text-xl sm:text-2xl font-black text-white">
                360<span className="text-[#F2780C]">°</span>
              </div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-wider">
                Solutions
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Services Ticker */}
      <div className="relative z-10 border-t border-white/10 bg-[#0D0D0D]/80 backdrop-blur-sm">
        <div className="overflow-hidden py-3 sm:py-4">
          <div ref={tickerRef} className="flex animate-ticker whitespace-nowrap">
            {[...SERVICE_TAGS, ...SERVICE_TAGS].map((tag, idx) => (
              <div key={idx} className="flex items-center gap-4 sm:gap-6 mx-3 sm:mx-4 flex-shrink-0">
                <span className="text-xs sm:text-sm font-semibold text-white/40 uppercase tracking-wider">
                  {tag}
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#038C7F]/50 flex-shrink-0"></span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 animate-bounce">
          <div className="w-5 h-8 border-2 border-white/20 rounded-full flex justify-center pt-1.5">
            <div className="w-1 h-1.5 bg-white/40 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Video Showreel Modal */}
      {showReelOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
          onClick={() => setShowReelOpen(false)}
        >
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md animate-modalBgIn"></div>
          <div
            className="relative w-full max-w-5xl animate-modalIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowReelOpen(false)}
              className="absolute -top-12 right-0 sm:-top-14 sm:right-0 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl sm:text-2xl"></i>
            </button>

            {/* Modal Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#F2780C] rounded-lg flex items-center justify-center">
                <i className="ri-play-fill text-white text-sm"></i>
              </div>
              <div>
                <h3 className="text-white font-bold text-sm sm:text-base">Asili Visuals Showreel</h3>
                <p className="text-gray-400 text-[10px] sm:text-xs">Our best work across East Africa</p>
              </div>
            </div>

            {/* Video Container */}
            <div className="relative w-full rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border border-white/10" style={{ aspectRatio: '16/9' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Cinematic%20showreel%20montage%20collage%20of%20professional%20advertising%20agency%20work%20including%20billboard%20campaigns%20corporate%20branding%20film%20production%20garment%20manufacturing%20creative%20agency%20portfolio%20dramatic%20lighting%20dark%20moody%20atmosphere%20premium%20quality%20East%20African%20creative%20work%20compilation&width=1280&height=720&seq=showreel-thumb-001&orientation=landscape"
                alt="Asili Visuals Showreel"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/40"></div>

              {/* Play Overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="relative mb-4">
                  <div className="absolute inset-0 bg-[#F2780C] rounded-full animate-ping opacity-20 scale-150"></div>
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-[#F2780C] rounded-full flex items-center justify-center shadow-2xl shadow-[#F2780C]/30 cursor-pointer hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-2xl sm:text-3xl text-white ml-1"></i>
                  </div>
                </div>
                <p className="text-white/80 text-xs sm:text-sm font-medium">Click to play showreel</p>
              </div>

              {/* Bottom Info Bar */}
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-time-line text-white/60 text-xs"></i>
                      <span className="text-[10px] sm:text-xs text-white/60">2:45</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-film-line text-white/60 text-xs"></i>
                      <span className="text-[10px] sm:text-xs text-white/60">HD Quality</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {['Branding', 'Film', 'Outdoor', 'Garments'].map((tag) => (
                      <span
                        key={tag}
                        className="hidden sm:inline-block px-2 py-0.5 bg-white/10 rounded text-[9px] font-semibold text-white/50 uppercase tracking-wider"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Showreel Highlights */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 mt-3 sm:mt-4">
              {[
                { label: 'Brand Campaigns', icon: 'ri-palette-line', img: 'https://readdy.ai/api/search-image?query=Professional%20brand%20identity%20design%20showcase%20logo%20design%20brand%20guidelines%20color%20palette%20modern%20creative%20agency%20work%20premium%20quality%20photography%20clean%20background&width=320&height=180&seq=reel-thumb-01&orientation=landscape' },
                { label: 'Film Production', icon: 'ri-film-line', img: 'https://readdy.ai/api/search-image?query=Professional%20film%20production%20behind%20the%20scenes%20cinema%20camera%20crew%20filming%20corporate%20documentary%20cinematic%20lighting%20production%20set%20premium%20quality%20photography&width=320&height=180&seq=reel-thumb-02&orientation=landscape' },
                { label: 'Outdoor Ads', icon: 'ri-billboard-line', img: 'https://readdy.ai/api/search-image?query=Large%20billboard%20advertising%20campaign%20on%20major%20highway%20urban%20landscape%20modern%20city%20professional%20outdoor%20advertising%20installation%20premium%20quality%20photography%20clear%20sky&width=320&height=180&seq=reel-thumb-03&orientation=landscape' },
                { label: 'Garment Branding', icon: 'ri-t-shirt-line', img: 'https://readdy.ai/api/search-image?query=Professional%20branded%20corporate%20uniforms%20apparel%20display%20quality%20textile%20production%20modern%20garment%20manufacturing%20branded%20clothing%20premium%20quality%20photography%20clean%20background&width=320&height=180&seq=reel-thumb-04&orientation=landscape' },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="group relative rounded-lg overflow-hidden cursor-pointer"
                  style={{ aspectRatio: '16/9' }}
                >
                  <img
                    src={item.img}
                    alt={item.label}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors"></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <i className={`${item.icon} text-white text-sm sm:text-lg mb-1`}></i>
                    <span className="text-[8px] sm:text-[10px] font-bold text-white uppercase tracking-wider text-center px-1">
                      {item.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Inline CSS animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); opacity: 0.3; }
          50% { transform: translateY(-20px); opacity: 0.6; }
        }
        @keyframes fadeSlideUp {
          0% { opacity: 0; transform: translateY(8px); }
          15% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        .animate-fadeSlideUp {
          animation: fadeSlideUp 2.8s ease-in-out;
        }
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker {
          animation: ticker 30s linear infinite;
        }
        @keyframes modalBgIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-modalBgIn {
          animation: modalBgIn 0.3s ease-out;
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.92) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modalIn {
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </section>
  );
}
